import type { Drill, Hint } from "./types.ts";
import { HARNESS_KIT } from "./harness-kit.ts";
import { FAKE_CLOCK_PRELUDE } from "./fake-clock.ts";

const starter = `class RateLimiter {
  /**
   * @param capacity - most requests allowed in a burst
   * @param refillPerSecond - tokens added back per second
   */
  constructor(private capacity: number, private refillPerSecond: number) {}

  /** True if this request is allowed (and consumes a token). */
  allow(key: string): boolean {
    // TODO
    return true;
  }

  /** Tokens currently available for a key. */
  available(key: string): number {
    // TODO
    return 0;
  }
}
`;

const solution = `type Bucket = { tokens: number; lastRefill: number };

class RateLimiter {
  private buckets = new Map<string, Bucket>();

  constructor(
    private capacity: number,
    private refillPerSecond: number,
  ) {}

  // Refill is computed from elapsed time on demand, never on a timer. A
  // setInterval drifts, does nothing while the process is asleep, and costs a
  // tick per key forever. Elapsed-time arithmetic is exact and costs nothing
  // for a key nobody is using.
  private bucketFor(key: string): Bucket {
    const now = Date.now();
    const bucket = this.buckets.get(key) ?? { tokens: this.capacity, lastRefill: now };
    const elapsedSeconds = (now - bucket.lastRefill) / 1000;
    if (elapsedSeconds > 0) {
      // Capped at capacity: an idle key must not bank hours of allowance and
      // then spend it all at once.
      bucket.tokens = Math.min(this.capacity, bucket.tokens + elapsedSeconds * this.refillPerSecond);
      bucket.lastRefill = now;
    }
    this.buckets.set(key, bucket);
    return bucket;
  }

  allow(key: string): boolean {
    const bucket = this.bucketFor(key);
    if (bucket.tokens < 1) return false;
    bucket.tokens -= 1;
    return true;
  }

  available(key: string): number {
    return Math.floor(this.bucketFor(key).tokens);
  }
}`;

const harness = HARNESS_KIT + `
if (typeof RateLimiter !== "function") {
  throw new Error("RateLimiter is not defined. Keep the class name as given.");
}

function __allowN(limiter, key, n) {
  const results = [];
  for (let i = 0; i < n; i++) results.push(limiter.allow(key));
  return results;
}

const __tests = [
  {
    name: "allows a burst up to capacity",
    about: "A full bucket lets a burst through; that is the point of a bucket rather than a fixed rate.",
    run: function () {
      const r = new RateLimiter(3, 1);
      __expectExact("capacity 3; allow x3", function () { return __allowN(r, "u1", 3); }, [true, true, true]);
    }
  },
  {
    name: "refuses once the bucket is empty",
    about: "The fourth request in a burst of three has nothing left to spend.",
    run: function () {
      const r = new RateLimiter(3, 1);
      __allowN(r, "u1", 3);
      __expectExact("capacity 3; the 4th call", function () { return r.allow("u1"); }, false);
    }
  },
  {
    name: "keys are limited independently",
    about: "One noisy client must not exhaust everyone else's allowance.",
    run: function () {
      const r = new RateLimiter(2, 1);
      __allowN(r, "noisy", 2);
      __expectExact("noisy exhausted; quiet's first call", function () { return r.allow("quiet"); }, true);
    }
  },
  {
    name: "reports what is available",
    about: "Callers need this for a Retry-After header, so it must track the same state allow() spends.",
    run: function () {
      const r = new RateLimiter(5, 1);
      r.allow("u1");
      r.allow("u1");
      __expectExact("capacity 5, two spent", function () { return r.available("u1"); }, 3);
    }
  },
  {
    name: "refills over time",
    hidden: true,
    about: "The bucket has to come back, or it is a quota rather than a rate limit.",
    run: function () {
      const r = new RateLimiter(2, 1);
      __allowN(r, "u1", 2);
      __clock.advance(1000);
      __expectExact("empty, then +1s at 1/sec", function () { return r.allow("u1"); }, true);
    }
  },
  {
    name: "refill is proportional to elapsed time",
    hidden: true,
    about: "Half a second at 2 per second is one token. Refilling a whole bucket on any elapsed time makes the limit meaningless.",
    run: function () {
      const r = new RateLimiter(10, 2);
      __allowN(r, "u1", 10);
      __clock.advance(500);
      __expectExact("emptied 10, +500ms at 2/sec", function () { return r.available("u1"); }, 1);
    }
  },
  {
    name: "never refills beyond capacity",
    hidden: true,
    about: "An idle key must not bank an hour of allowance and spend it in one burst. This is the difference between a rate limit and a delayed stampede.",
    run: function () {
      const r = new RateLimiter(3, 5);
      __clock.advance(60_000);
      __expectExact("capacity 3, idle for a minute", function () { return r.available("u1"); }, 3);
      __expectExact("... allow x4", function () { return __allowN(r, "u1", 4); }, [true, true, true, false]);
    }
  },
  {
    name: "an unseen key starts full",
    hidden: true,
    about: "A first-time caller should not be throttled because their bucket was created empty.",
    run: function () {
      const r = new RateLimiter(4, 1);
      __expectExact("brand new key", function () { return r.available("never-seen"); }, 4);
    }
  },
  {
    name: "partial tokens accumulate rather than being lost",
    hidden: true,
    about: "Rounding down on every refill instead of storing a fraction means a slow refill rate never refills at all. Two 500ms gaps at 1/sec must add up to one token.",
    run: function () {
      const r = new RateLimiter(5, 1);
      __allowN(r, "u1", 5);
      __clock.advance(500);
      r.available("u1");
      __clock.advance(500);
      __expectExact("emptied, then two 500ms gaps at 1/sec", function () { return r.available("u1"); }, 1);
    }
  },
  {
    name: "checking availability does not consume",
    hidden: true,
    about: "available() is a read. If it spends a token, every dashboard that displays the limit changes it.",
    run: function () {
      const r = new RateLimiter(3, 1);
      r.available("u1");
      r.available("u1");
      __expectExact("two availability checks, then allow x3", function () { return __allowN(r, "u1", 3); }, [true, true, true]);
    }
  },
  {
    name: "a refused request does not consume either",
    hidden: true,
    about: "Decrementing on a refusal pushes the bucket negative, so a client hammering a limit takes ever longer to recover. That is a lockout, not a limit.",
    run: function () {
      const r = new RateLimiter(1, 1);
      r.allow("u1");
      for (let i = 0; i < 20; i++) r.allow("u1");
      __clock.advance(1000);
      __expectExact("20 refusals then +1s at 1/sec", function () { return r.allow("u1"); }, true);
    }
  }
];
`;

const hints: Hint[] = [
  {
    label: "Nudge",
    body: "You do not need a timer to refill a bucket. What can you work out from the time elapsed since that key was last touched? A setInterval drifts, does nothing while the process is asleep, and costs a tick per key forever.",
    code: `type Bucket = { tokens: number; lastRefill: number };

class RateLimiter {
  private buckets = new Map<string, Bucket>();

  constructor(
    private capacity: number,
    private refillPerSecond: number,
  ) {}
}`,
  },
  {
    label: "Approach",
    body: "One helper brings a key's bucket up to date, and both public methods go through it. Add elapsed times rate, cap at capacity, move the timestamp, and only then decide whether there is a token to take. An unseen key starts full, or a first-time caller is throttled for no reason.",
    code: `private bucketFor(key: string): Bucket {
  const now = Date.now();
  const bucket = this.buckets.get(key) ?? { tokens: this.capacity, lastRefill: now };
  const elapsedSeconds = (now - bucket.lastRefill) / 1000;
  if (elapsedSeconds > 0) {
    // Fractional tokens are KEPT, not floored: two 500ms gaps at 1/sec must
    // add up to one token, or a slow refill rate never refills at all.
    bucket.tokens = Math.min(this.capacity, bucket.tokens + elapsedSeconds * this.refillPerSecond);
    bucket.lastRefill = now;
  }
  this.buckets.set(key, bucket);
  return bucket;
}

available(key: string): number {
  return Math.floor(this.bucketFor(key).tokens);   // a read, so it must not spend
}`,
  },
  {
    label: "The trap",
    body: "Cap the refill at capacity or a client that was quiet all morning gets to flood you, which is a delayed stampede rather than a rate limit. And a refused request must not consume anything: decrement on a refusal and a client retrying in a loop drives its own bucket negative and locks itself out for longer and longer.",
  },
];

export const rateLimiter: Drill = {
  id: "rate-limiter",
  track: "typescript",
  title: "Token-bucket rate limiter",
  pattern: "rate-limiting",
  difficulty: "medium",
  basePoints: 250,
  timeBudgetMs: 25 * 60 * 1000,
  prelude: FAKE_CLOCK_PRELUDE,
  statement: `Your API allows each client a burst of requests and then a steady rate. Implement a token bucket.

\`\`\`ts
class RateLimiter {
  constructor(capacity: number, refillPerSecond: number);
  allow(key: string): boolean;      // consumes a token if one is available
  available(key: string): number;   // tokens left, without consuming
}
\`\`\`

Each key has a bucket holding at most \`capacity\` tokens. A request costs one. Tokens refill at \`refillPerSecond\` and the bucket never holds more than \`capacity\`.

## Requirements

- **A burst up to capacity is allowed**, then refused until tokens return.
- **Refill is proportional to elapsed time**, so half a second at 2/sec is one token.
- **Never exceed capacity**, however long a key has been idle.
- **Keys are independent**, and an unseen key starts full.
- **\`available\` does not consume**, and neither does a refused request.

## Example

\`\`\`ts
const limiter = new RateLimiter(3, 1);   // burst of 3, then 1 per second

limiter.allow("user-42");   // true
limiter.allow("user-42");   // true
limiter.allow("user-42");   // true
limiter.allow("user-42");   // false — bucket empty
// one second later
limiter.allow("user-42");   // true
\`\`\`

## Where this gets interesting

The instinct is a \`setInterval\` that tops every bucket up. Do not. It drifts, it does nothing while the process is suspended, it costs a tick per key forever, and it needs cleanup for keys nobody uses any more. Compute the refill from elapsed time when the key is next touched: exact, free for idle keys, and correct across a suspend.

Then the two that bite. An idle bucket must **cap at capacity**, or a client that was quiet all morning gets to flood you. And a **refused request must not consume**, or a client retrying in a loop drives its own bucket negative and locks itself out for longer and longer.

\`Date.now\` is on a fake clock here, so the tests are instant.`,
  hints,
  starter,
  harness,
  solution,
  complexity:
    "O(1) per call, and one bucket per key. Lazy refill means an idle key costs nothing; add an eviction policy if the key space is unbounded, and say so out loud.",
};
